/**
 * 커피챗 배너 관리 API
 */

import { apiClient } from './apiClient';

// 커피챗 배너 타입 정의
export interface CoffeeChatBanner {
  banner_id: number;
  title: string;
  description: string;
  font_color_hex_code: string;
  banner_image_url: string;
}

// 커피챗 섹션 관련 인물 타입 정의
export interface CoffeeChatSectionRelatedPerson {
  section_related_person_id: number;
  person_id: number;
  name: string;
  department: string;
  image_url: string;
  tags: string[];
}

// 커피챗 섹션 타입 정의
export interface CoffeeChatSection {
  coffee_chat_section_id: number;
  title: string;
  text: string;
  related_persons: CoffeeChatSectionRelatedPerson[];
}

// 커피챗 UI 데이터 전체 응답 타입 (API 명세에 맞춤)
export interface CoffeeChatUIResponse {
  code: number;
  status: string;
  message: string;
  data: {
    banners: CoffeeChatBanner[];
    sections: CoffeeChatSection[];
  };
}

export interface CoffeeChatBannerCreateRequest {
  title: string;
  description: string;
  fontcolorHexCode: string;
  bannerImage: File;
}

export interface CoffeeChatBannerUpdateRequest {
  title: string;
  description: string;
  fontcolorHexCode: string;
  bannerImage: File;
}

// 관리자용 커피챗 UI 데이터 조회 (배너 + 섹션 정보 통합 조회)
export const getAdminCoffeeChatUI = async (): Promise<CoffeeChatUIResponse> => {
  try {
    const response = await apiClient.get('/api/admin/ui/coffee-chat');
    return response.data;
  } catch (error: any) {
    throw new Error(error.message || '관리자 커피챗 UI 데이터를 불러올 수 없습니다.');
  }
};

// 커피챗 배너 목록 조회
export const getCoffeeChatBannerList = async (): Promise<CoffeeChatBanner[]> => {
  try {
    const response = await apiClient.get('/api/ui/coffee-chat/banners');
    if (response.data && response.data.data) {
      return Array.isArray(response.data.data) ? response.data.data : [response.data.data];
    }
    return [];
  } catch (error: any) {
    if (error.response?.status === 404) {
      return [];
    }
    throw new Error(error.message || '커피챗 배너 목록을 불러올 수 없습니다.');
  }
};

// 커피챗 배너 생성
export const createCoffeeChatBanner = async (bannerData: CoffeeChatBannerCreateRequest): Promise<CoffeeChatBanner> => {
  try {
    const formData = new FormData();
    formData.append('title', bannerData.title);
    formData.append('description', bannerData.description);
    formData.append('fontcolorHexCode', bannerData.fontcolorHexCode);
    formData.append('bannerImage', bannerData.bannerImage);

    const response = await apiClient.post('/api/ui/coffee-chat/banners', formData);
    if (!response.data || !response.data.data) {
      throw new Error('커피챗 배너 생성 응답이 올바르지 않습니다.');
    }
    return response.data.data;
  } catch (error: any) {
    throw new Error(error.message || '커피챗 배너 생성에 실패했습니다.');
  }
};

// 커피챗 배너 수정
export const updateCoffeeChatBanner = async (bannerId: number, bannerData: CoffeeChatBannerUpdateRequest): Promise<CoffeeChatBanner> => {
  try {
    const formData = new FormData();
    formData.append('title', bannerData.title);
    formData.append('description', bannerData.description);
    formData.append('fontcolorHexCode', bannerData.fontcolorHexCode);
    formData.append('bannerImage', bannerData.bannerImage);

    const response = await apiClient.put(`/api/ui/coffee-chat/banners/${bannerId}`, formData);
    if (!response.data || !response.data.data) {
      throw new Error('커피챗 배너 수정 응답이 올바르지 않습니다.');
    }
    return response.data.data;
  } catch (error: any) {
    throw new Error(error.message || '커피챗 배너 수정에 실패했습니다.');
  }
};

// 커피챗 배너 삭제
export const deleteCoffeeChatBanner = async (bannerId: number): Promise<void> => {
  try {
    await apiClient.delete(`/api/ui/coffee-chat/banners/${bannerId}`);
  } catch (error: any) {
    throw new Error(error.message || '커피챗 배너 삭제에 실패했습니다.');
  }
};

// =============================================================================
// 커피챗 큐레이션 관련 API (사용자 요청 스펙에 맞춤)
// =============================================================================

// 멘토 정보 타입 정의
export interface MentorInfo {
  id: number;
  name: string;
  email: string;
  department: string;
  tags: string[];
  image_url: string;
  banner_url: string;
  introduction: string;
  is_temp: boolean;
  created_at: string;
}

// 멘토 목록 응답 타입
export interface MentorListResponse {
  totalElements: number;
  totalPages: number;
  size: number;
  content: MentorInfo[];
  number: number;
  sort: {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
  };
  first: boolean;
  last: boolean;
  numberOfElements: number;
  pageable: {
    offset: number;
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    paged: boolean;
    pageNumber: number;
    pageSize: number;
    unpaged: boolean;
  };
  empty: boolean;
}

// 큐레이션 생성/수정 요청 타입
export interface CurationRequest {
  title: string;
  text: string;
}

// 큐레이션 응답 타입
export interface CurationResponse {
  id: number; // backend: CoffeeChatSectionResDto.id
  coffee_chat_section_id?: number; // legacy alias
  title: string;
  text: string;
}

// 큐레이션 관련 인물 정보 타입
export interface CurationPersonInfo {
  section_related_person_id: number;
  person_id: number;
  name: string;
  department: string;
  image_url: string;
  tags: string | string[];
}

// 큐레이션에 멘토 추가 응답 타입
export interface CurationPersonResponse {
  section_related_person_id: number;
  relation_id: number;
  person_id: number;
  name: string;
  department: string;
  image_url: string;
  tags: string[];
}

// 멘토 목록 조회 (GET /api/admin/mentors/list)
export const getCoffeeChatMentorList = async (
  isTemp?: boolean,
  from?: string,
  to?: string,
  page: number = 0,
  size: number = 10
): Promise<MentorListResponse> => {
  try {
    const params: Record<string, any> = { page, size };
    if (isTemp !== undefined) params.isTemp = isTemp;
    if (from) params.from = from;
    if (to) params.to = to;

    const response = await apiClient.get('/api/admin/mentors/list', { params });
    return response.data.data;
  } catch (error: any) {
    throw new Error(error.message || '멘토 목록을 불러올 수 없습니다.');
  }
};

// 큐레이션 섹션 정보 타입 (목록 조회용)
export interface CurationSectionInfo {
  coffee_chat_section_id: number;
  title: string;
  text: string;
  related_persons: CurationPersonInfo[];
}

// 큐레이션 목록 조회 응답 타입
export interface CurationListResponse {
  sections: CurationSectionInfo[];
}

// 큐레이션 목록 조회 (GET /api/ui/coffee-chat/curations/persons/list)
export const getCoffeeChatCurationList = async (): Promise<CurationListResponse> => {
  try {
    const response = await apiClient.get('/api/ui/coffee-chat/curations/persons/list');
    return response.data.data;
  } catch (error: any) {
    throw new Error(error.message || '커피챗 큐레이션 목록을 불러올 수 없습니다.');
  }
};

// 큐레이션 등록 (POST /api/ui/coffee-chat/curations)
export const createCoffeeChatCuration = async (curationData: CurationRequest): Promise<CurationResponse> => {
  try {
    const response = await apiClient.post('/api/ui/coffee-chat/curations', curationData);
    return response.data.data;
  } catch (error: any) {
    throw new Error(error.message || '큐레이션 등록에 실패했습니다.');
  }
};

// 큐레이션 수정 (PUT /api/ui/coffee-chat/curations/{curationId})
export const updateCoffeeChatCuration = async (curationId: number, curationData: CurationRequest): Promise<CurationResponse> => {
  try {
    const response = await apiClient.put(`/api/ui/coffee-chat/curations/${curationId}`, curationData);
    return response.data.data;
  } catch (error: any) {
    throw new Error(error.message || '큐레이션 수정에 실패했습니다.');
  }
};

// 큐레이션 삭제 (DELETE /api/ui/coffee-chat/curations/{curationId})
export const deleteCoffeeChatCuration = async (curationId: number): Promise<void> => {
  try {
    await apiClient.delete(`/api/ui/coffee-chat/curations/${curationId}`);
  } catch (error: any) {
    throw new Error(error.message || '큐레이션 삭제에 실패했습니다.');
  }
};

// 큐레이션에 멘토 등록 (POST /api/ui/coffee-chat/curations/{curationId}/persons/{personId})
export const addPersonToCoffeeChatCuration = async (curationId: number, personId: number): Promise<CurationPersonResponse> => {
  try {
    const response = await apiClient.post(`/api/ui/coffee-chat/curations/${curationId}/persons/${personId}`);
    return response.data.data;
  } catch (error: any) {
    throw new Error(error.message || '멘토 등록에 실패했습니다.');
  }
};

// 큐레이션에서 멘토 삭제 (DELETE /api/ui/coffee-chat/curations/{curationId}/persons/{personId})
export const removePersonFromCoffeeChatCuration = async (curationId: number, personId: number): Promise<void> => {
  try {
    await apiClient.delete(`/api/ui/coffee-chat/curations/${curationId}/persons/${personId}`);
  } catch (error: any) {
    throw new Error(error.message || '멘토 삭제에 실패했습니다.');
  }
};
